import React, { useState, useEffect } from "react";
import { H2, Text } from "../../../../../components/typography";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Button } from "@amzn/stencil-react-components/button";
import { UploadCard } from "../upload-card";
import { Input } from "@amzn/stencil-react-components/form";
import useArb from "../../../../../hooks/use-arb";
import { AttachmentType, UploadDetails } from "../../models";
import { submitDocument, updateDocumentId } from "../../requests/requests";
import { useParams, useHistory } from "react-router-dom";
import { AttachmentSubType } from "../../models";
import {
  deleteCachedRequest,
  getCandidateData,
} from "../../../../../utility/candidate-data";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";

export function TaxIdUploadLayout() {
  const bundle = useArb("HVHChecklistPantherResource");
  const taxIdTitleMessage = bundle.getMessage("Checklist-Tax-ID-Title");
  const taxIdTextMessage = bundle.getMessage("Checklist-Tax-ID-Text");
  const taxIdNumberMessage = bundle.getMessage("Checklist-Tax-ID-Number-Title");
  const imageSubmitMessage = bundle.getMessage(
    "Checklist-Document-Image-Submit-ButtonText"
  );
  const maximumFileSizeMessage = bundle.getMessage(
    "Checklist-Document-Maximum-File-Size-Text"
  );
  const acceptableFileText = bundle.getMessage(
    "Checklist-Document-Acceptable-File-Text"
  );
  const documentUploadDependencyServiceErrorMessage = bundle.getMessage(
    "Checklist-Document-General-Error-Message"
  );
  const documentIdUploadDependencyServiceErrorMessage = bundle.getMessage(
    "Checklist-Document-ID-General-Error-Message"
  );
  const p0LimitFixMessage = bundle.getMessage("p0LimitFixMessage");
  const [frontDocument, setFrontDocument] = useState<UploadDetails>();
  const [submitButtonToggle, setBlockSubmitButton] = useState<boolean>(true);
  const [
    fileUploadDependencyServiceErrorMessage,
    setFileUploadDependencyServiceErrorMessage,
  ] = useState<string>();
  const [
    idUploadDependencyServiceErrorMessage,
    setIdUploadDependencyServiceErrorMessage,
  ] = useState<string>();
  const [taxIdNumber, setTaxIdNumber] = useState<string>("");
  const { requisitionId, applicationId } = useParams<{
    requisitionId: string;
    applicationId: string;
  }>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const history = useHistory();

  const backToMainPage = () => {
    const path = `/checklist/${requisitionId}/${applicationId}`;
    history.push(path);
  };
  const validateTaxNumber = new RegExp("^[a-zA-Z0-9]{13}$");

  useEffect(() => {
    if (frontDocument?.imageBase64 && validateTaxNumber.test(taxIdNumber)) {
      setBlockSubmitButton(false);
    } else {
      setBlockSubmitButton(true);
    }
  }, [frontDocument, taxIdNumber]);

  const updateDocument = async (
    image: string,
    fileName: string,
    contentType: string,
    side: string,
    applicationId?: string
  ) => {
    let candidateId = "";
    if (applicationId) {
      const candidateData = await getCandidateData(applicationId);
      candidateId = candidateData?.candidateId;
    }
    const documentData = {
      documentType: AttachmentType.RFC,
      attachmentSubType: side,
      imageBase64: image,
      fileName: fileName,
      contentType: contentType,
      candidateId: candidateId,
    };
    setFrontDocument(documentData);
  };

  const clickEventSubmitDocument = async () => {
    let returnToMainPage = true;
    if (frontDocument && validateTaxNumber.test(taxIdNumber)) {
      const candidateId = frontDocument.candidateId;
      try {
        await updateDocumentId({
          applicationId,
          idData: {
            nationalIdType: AttachmentType.RFC,
            idNumber: taxIdNumber,
          },
          candidateId,
        });
      } catch (err) {
        console.log(err);
        setIdUploadDependencyServiceErrorMessage(
          documentIdUploadDependencyServiceErrorMessage
        );
        returnToMainPage = false;
      }
      try {
        await submitDocument(
          applicationId,
          [frontDocument],
          setBlockSubmitButton
        );
      } catch (err) {
        console.log(err);
        setBlockSubmitButton(false);
        setFileUploadDependencyServiceErrorMessage(
          documentUploadDependencyServiceErrorMessage
        );
        returnToMainPage = false;
      }
      deleteCachedRequest(applicationId); // delete stale data so that updated Id loads on home page
      if (returnToMainPage) {
        backToMainPage();
      }
    }
  };
  return (
    <Col
      padding={"10px"}
      display={"flex"}
      justifyContent={"center"}
      gridGap={6}
      maxWidth={"330px"}
    >
      <H2>{taxIdTitleMessage}</H2>
      <Text>
        {taxIdTextMessage} <strong style={{ color: "red" }}>*</strong>{" "}
      </Text>
      <Text>{acceptableFileText}</Text>
      <Text>{maximumFileSizeMessage}</Text>
      <Row
        alignSelf={"center"}
        padding={{ top: "10px", bottom: "10px" }}
        gridGap="S400"
      >
        <UploadCard
          side={AttachmentSubType.FRONT}
          documentDetailsFn={updateDocument}
          setErrorMessage={setErrorMessage}
          applicationId={applicationId}
          setDependencyErrorMessage={setFileUploadDependencyServiceErrorMessage}
        />
      </Row>

      <Col>
        <Text>
          {taxIdNumberMessage} <strong style={{ color: "red" }}>*</strong>
        </Text>{" "}
        <Input
          placeholder={taxIdNumberMessage}
          maxLength={13}
          onChange={(e) => setTaxIdNumber(e.target.value)}
          type="text"
        ></Input>
        {/*This place holder text needs to be updated when the message gets added  */}
      </Col>
      {errorMessage || fileUploadDependencyServiceErrorMessage ? (
        <MessageBanner type={MessageBannerType.Error}>
          {fileUploadDependencyServiceErrorMessage ?? errorMessage}
        </MessageBanner>
      ) : null}
      {idUploadDependencyServiceErrorMessage ? (
        <MessageBanner type={MessageBannerType.Error}>
          {idUploadDependencyServiceErrorMessage}
        </MessageBanner>
      ) : null}
      <Row padding={{ top: "10px", bottom: "10px" }}>
        <MessageBanner type={MessageBannerType.Informational}>
          {p0LimitFixMessage}
        </MessageBanner>
      </Row>
      <Button
        onClick={clickEventSubmitDocument}
        aria-disabled={submitButtonToggle}
      >
        {imageSubmitMessage}
      </Button>
    </Col>
  );
}
